import {
  Box,
  Button,
  ButtonVariants,
  Card,
  FormControlSizes,
  HStack,
  IconName,
  Text,
  useStrategiesContext,
} from '@talos/kyoko';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import type { PreTradeResponseData } from '../../../redux/preTradeApiTypes';
import { SnipeSweepBoundsChart } from './SnipeSweepBoundsChart';
import { useCompareUrgencies } from './useCompareUrgencies';
import { buildBandFromScenario, formatBandLegendLabel, isSniperScenario, type SnipeSweepBand } from './utils';

const CHART_HEIGHT = 280;

const LegendSwatch = styled(Box)<{ $color: string }>`
  width: 24px;
  height: 0;
  border-top: 2px dashed ${({ $color }) => $color};
  flex-shrink: 0;
`;

interface SnipeSweepBoundsProps {
  scenario?: PreTradeResponseData;
}

/**
 * Arrival-aware snipe/sweep band chart, gated on `Metadata.PreTradeType === 'Sniper'`.
 * The "Compare" action fans out one pre-trade request per urgency and overlays them.
 */
export function SnipeSweepBounds({ scenario }: SnipeSweepBoundsProps) {
  const theme = useTheme();
  const { strategiesByName } = useStrategiesContext();
  const compare = useCompareUrgencies(scenario);

  const view = useMemo(() => {
    if (!scenario || !isSniperScenario(scenario, strategiesByName)) {
      return null;
    }
    const baseBand = buildBandFromScenario(scenario, scenario.Strategy ?? 'Scenario', theme.colors.blue.lighten);
    const isComparing = compare.bands.length > 0;
    const visibleBands: SnipeSweepBand[] = isComparing ? compare.bands : baseBand ? [baseBand] : [];
    return { isComparing, visibleBands };
  }, [scenario, strategiesByName, theme, compare.bands]);

  if (!view) {
    return null;
  }
  const { isComparing, visibleBands } = view;

  return (
    <Card
      title="Snipe / Sweep Bounds"
      data-testid="snipe-sweep-bounds-card"
      actions={
        <SniperCardActions
          isComparing={isComparing}
          isLoading={compare.isLoading}
          canCompare={compare.canCompare}
          onCompare={compare.compare}
          onReset={compare.reset}
        />
      }
    >
      {compare.error && (
        <Box mb="spacing8">
          <Text color="colorTextNegative" fontSize="fontSizeSmall">
            {compare.error}
          </Text>
        </Box>
      )}
      <Box h={`${CHART_HEIGHT}px`}>
        {visibleBands.length > 0 ? (
          <SnipeSweepBoundsChart bands={visibleBands} />
        ) : (
          <Box h="100%" display="flex" alignItems="center" justifyContent="center">
            <Text color="colorTextSubtle">No snipe / sweep bounds returned by the pre-trade response yet.</Text>
          </Box>
        )}
      </Box>
      <BandsLegend bands={visibleBands} />
    </Card>
  );
}

interface SniperCardActionsProps {
  isComparing: boolean;
  isLoading: boolean;
  canCompare: boolean;
  onCompare: () => void;
  onReset: () => void;
}

function SniperCardActions({ isComparing, isLoading, canCompare, onCompare, onReset }: SniperCardActionsProps) {
  if (isComparing) {
    return (
      <Button
        variant={ButtonVariants.Default}
        size={FormControlSizes.Small}
        startIcon={IconName.Close}
        onClick={onReset}
        data-testid="snipe-sweep-bounds-reset"
      >
        Reset
      </Button>
    );
  }
  return (
    <Button
      variant={ButtonVariants.Default}
      size={FormControlSizes.Small}
      onClick={onCompare}
      loading={isLoading}
      disabled={isLoading || !canCompare}
      data-testid="snipe-sweep-bounds-compare"
    >
      Compare
    </Button>
  );
}

interface BandsLegendProps {
  bands: SnipeSweepBand[];
}

function BandsLegend({ bands }: BandsLegendProps) {
  if (bands.length === 0) {
    return null;
  }
  return (
    <HStack gap="spacing16" justifyContent="center" flexWrap="wrap" data-testid="snipe-sweep-bounds-legend">
      {bands.map(band => (
        <HStack
          key={band.label}
          gap="spacing8"
          alignItems="center"
          data-testid={`snipe-sweep-bounds-legend-${band.label}`}
        >
          <LegendSwatch $color={band.color} aria-hidden />
          <Text fontSize="fontSizeSmall" color="colorTextDefault">
            {formatBandLegendLabel(band)}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}
